/**
 * 1+1 Gift Popup for product cards (Deal C).
 *
 * Intercepts card add-to-cart clicks (both the card button handled by the
 * <custom-product> element and the ATC inside the cloned swatches popup) for
 * products that rendered a [data-gift-popup] template, and opens a modal where
 * the customer picks a free variant of the same product. The CTA posts parent
 * + gift in ONE /cart/add.js request with Deal C line item properties — the
 * external deal-discount Function validates the linkage and discounts the gift.
 * Closing the modal adds only the parent (normal add, no deal properties).
 *
 * Interception happens with a document-level CAPTURE listener so it runs before
 * the theme's own button/submit handlers (custom-product click handler and
 * attachPopupProductFormHandler in theme.js).
 */
(function () {
  'use strict';

  const ADD_URL = '/cart/add.js';
  let activeModal = null;

  function makeSessionId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  /* ---------------- interception ---------------- */

  document.addEventListener('click', onDocumentClick, true);

  function onDocumentClick(e) {
    // Drawer upsells in 1+1 mode: the button carries data-gift-one-plus-one
    // (the gift discount pct) — add the selected variant + a free unit of the
    // same variant in one request, beating the theme's own addItemById
    // handler via this capture-phase listener.
    const upsellBtn = e.target.closest('button.cross-sells__item-add[data-gift-one-plus-one]');
    if (upsellBtn && !upsellBtn.disabled) {
      const upsellVariantId = parseInt(upsellBtn.dataset.addItemId, 10);
      if (Number.isFinite(upsellVariantId)) {
        e.preventDefault();
        e.stopPropagation();
        directAdd(
          upsellBtn,
          { variantId: upsellVariantId, quantity: 1 },
          upsellVariantId,
          upsellBtn.dataset.giftOnePlusOne || '100'
        );
      }
      return;
    }

    const btn = e.target.closest(
      'button.product-form__cart-submit, button.product-form__cart-submit-swatches-popup'
    );
    if (!btn || btn.disabled) return;
    // Ignore the PDP deal-selector button (same theme classes, own flow).
    if (btn.closest('deal-selector')) return;

    const context = resolvePurchaseContext(btn);
    if (!context || !context.template) return;
    const template = context.template;

    e.preventDefault();
    e.stopPropagation();

    // If triggered from the cloned swatches popup, close it first.
    const swatchesPopup = btn.closest('.product-item__variant-info-popup.active-swatches-popup');
    if (swatchesPopup) {
      const closeBtn = swatchesPopup.querySelector('.product-title-close_button');
      if (closeBtn) closeBtn.click();
    }

    // Exactly one pickable variant — nothing to choose, add both units directly.
    const singleVariantId = parseInt(template.dataset.singleVariantId, 10);
    if (Number.isFinite(singleVariantId)) {
      directAdd(btn, context, singleVariantId, template.dataset.discountPct || '100');
      return;
    }

    openModal(template, context);
  }

  /**
   * Popup-less path for single-variant gift pools: parent + gift in one
   * request. If that fails (e.g. only 1 unit left in stock), fall back to a
   * plain add of the parent alone so the customer's click still lands.
   */
  async function directAdd(btn, context, giftVariantId, discountPct) {
    if (btn.dataset.giftPopupBusy === '1') return;
    btn.dataset.giftPopupBusy = '1';
    btn.classList.add('loading');

    const sessionId = makeSessionId();
    // Gift first: the drawer lists the newest line on top, so the PAID
    // parent (processed last) shows above the free gift.
    const items = [
      {
        id: giftVariantId,
        quantity: 1,
        properties: {
          _gift_for: sessionId,
          _free_gift: 'Yes',
          _deal_discount_pct: String(discountPct),
        },
      },
      {
        id: context.variantId,
        quantity: context.quantity,
        properties: { _deal: 'c', _deal_session: sessionId },
      },
    ];

    try {
      let payload;
      try {
        payload = await postItems(items);
      } catch (err) {
        console.error('[gift-popup] direct 1+1 add failed, falling back to plain add:', err);
        payload = await postItems([{ id: context.variantId, quantity: context.quantity }]);
      }
      notifyCartUpdated(payload);
      // Clicking from inside the drawer (upsell buttons): the markup refresh
      // above is enough — synthesizing a cart-icon click could toggle the
      // already-open drawer closed.
      if (!btn.closest('.quick-cart')) {
        openQuickCart();
      }
    } catch (err) {
      console.error('[gift-popup] add error:', err);
    } finally {
      delete btn.dataset.giftPopupBusy;
      btn.classList.remove('loading');
    }
  }

  /**
   * Figure out which product/variant/qty the clicked ATC would add, and locate
   * the matching [data-gift-popup] template. The purchase variant comes from
   * the enclosing form's [name="id"], kept in sync by the theme's swatch
   * handlers. The product id comes from the card itself, or — for the ATC
   * inside the cloned swatches popup (detached from the card) — from the
   * popup's swatch list, then the template is found document-wide.
   * NOTE: the form's data-current-product-id is NOT usable here — the theme's
   * swatchSetActiveInPopup overwrites it with a variant id.
   */
  function resolvePurchaseContext(btn) {
    const form = btn.closest('form.product-form');
    if (!form) return null;

    let template = null;
    const card = btn.closest('.product-item');
    if (card) {
      template = card.querySelector('[data-gift-popup]');
    } else {
      const popup = btn.closest('.product-item__variant-info-popup');
      const list = popup && popup.querySelector('.product-swatches-options__list');
      const productId = list && list.dataset.productId;
      if (productId) {
        template = document.querySelector('[data-gift-popup][data-product-id="' + productId + '"]');
      }
    }
    if (!template) return null;

    const idInput = form.querySelector('[name="id"]');
    const variantId = idInput ? parseInt(idInput.value, 10) : NaN;
    if (!Number.isFinite(variantId)) return null;

    const qtyInput = form.querySelector('[name="quantity"]');
    const quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

    return { template: template, variantId: variantId, quantity: quantity };
  }

  /* ---------------- modal ---------------- */

  function openModal(template, context) {
    closeModal();

    const fragment = template.content.firstElementChild;
    if (!fragment) return;
    const modal = fragment.cloneNode(true);
    modal._giftContext = context;
    modal._discountPct = template.dataset.discountPct || '100';

    document.body.appendChild(modal);
    document.documentElement.classList.add('gift-popup-open');
    activeModal = modal;

    fillParentSummary(modal, context);
    bindModalEvents(modal);
  }

  function closeModal() {
    if (!activeModal) return;
    activeModal.remove();
    activeModal = null;
    document.documentElement.classList.remove('gift-popup-open');
  }

  function fillParentSummary(modal, context) {
    // The purchased variant is always present among the gift swatches (both
    // lists use the same visibility rules), so reuse their data attributes.
    const swatch = modal.querySelector(
      '[data-gift-popup-swatch][data-variant-id="' + context.variantId + '"]'
    );
    if (!swatch) return; // Default Title product — static markup already correct.

    const variantEl = modal.querySelector('[data-gift-popup-parent-variant]');
    if (variantEl) variantEl.textContent = swatch.dataset.variantTitle || '';

    const priceEl = modal.querySelector('[data-gift-popup-parent-price]');
    if (priceEl) priceEl.textContent = swatch.dataset.variantPrice || '';

    const img = modal.querySelector('[data-gift-popup-parent-image]');
    if (img && swatch.dataset.variantImage) img.src = swatch.dataset.variantImage;
  }

  function bindModalEvents(modal) {
    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-gift-popup-close-trigger]')) {
        onSkipGift(modal);
        return;
      }
      const swatch = e.target.closest('[data-gift-popup-swatch]');
      if (swatch) {
        selectGiftSwatch(modal, swatch);
        return;
      }
      const tab = e.target.closest('[data-gift-popup-tabs] .swatch-tab');
      if (tab) {
        applyTabFilter(modal, tab);
        return;
      }
      if (e.target.closest('[data-gift-popup-cta]')) {
        onAddWithGift(modal);
      }
    });
  }

  function selectGiftSwatch(modal, swatch) {
    modal.querySelectorAll('[data-gift-popup-swatch]').forEach(function (s) {
      s.classList.toggle('is-selected', s === swatch);
    });

    const titleEl = modal.querySelector('[data-gift-popup-selected-title]');
    if (titleEl) titleEl.textContent = swatch.dataset.variantTitle || '';

    const priceEl = modal.querySelector('[data-gift-popup-selected-price]');
    if (priceEl) priceEl.textContent = swatch.dataset.variantPrice || '';

    const swatchDisplay = modal.querySelector('[data-gift-popup-selected-swatch]');
    if (swatchDisplay) {
      const circle = swatch.querySelector('.gift-popup__swatch-circle');
      if (circle) swatchDisplay.innerHTML = circle.outerHTML;
    }
  }

  function applyTabFilter(modal, tab) {
    modal.querySelectorAll('[data-gift-popup-tabs] .swatch-tab').forEach(function (t) {
      t.classList.toggle('is-active', t === tab);
    });
    const allow = tab.dataset.tabName === 'all' ? null : new Set((tab.dataset.tabVariants || '').split(','));
    modal.querySelectorAll('[data-gift-popup-swatch]').forEach(function (s) {
      const visible = !allow || allow.has(s.dataset.variantId);
      s.classList.toggle('hidden', !visible);
    });
  }

  function getSelectedGiftVariantId(modal) {
    const hidden = modal.querySelector('input[type="hidden"][data-gift-popup-gift-variant]');
    if (hidden) return parseInt(hidden.value, 10);
    const selected = modal.querySelector('[data-gift-popup-swatch].is-selected');
    return selected ? parseInt(selected.dataset.variantId, 10) : NaN;
  }

  /* ---------------- cart ---------------- */

  function onAddWithGift(modal) {
    const context = modal._giftContext;
    const giftVariantId = getSelectedGiftVariantId(modal);
    if (!context || !Number.isFinite(giftVariantId)) return;

    const sessionId = makeSessionId();
    // Gift first: the drawer lists the newest line on top, so the PAID
    // parent (processed last) shows above the free gift.
    const items = [
      {
        id: giftVariantId,
        quantity: 1,
        properties: {
          _gift_for: sessionId,
          _free_gift: 'Yes',
          _deal_discount_pct: String(modal._discountPct),
        },
      },
      {
        id: context.variantId,
        quantity: context.quantity,
        properties: { _deal: 'c', _deal_session: sessionId },
      },
    ];
    submitItems(modal, items);
  }

  function onSkipGift(modal) {
    // The customer did click "add to cart" — honor that with a plain add.
    const context = modal._giftContext;
    if (!context) {
      closeModal();
      return;
    }
    submitItems(modal, [{ id: context.variantId, quantity: context.quantity }]);
  }

  async function submitItems(modal, items) {
    const cta = modal.querySelector('[data-gift-popup-cta]');
    if (modal._busy) return;
    modal._busy = true;
    if (cta) {
      cta.disabled = true;
      cta.setAttribute('aria-busy', 'true');
    }
    showError(modal, '');

    try {
      const payload = await postItems(items);

      closeModal();
      notifyCartUpdated(payload);
      openQuickCart();
    } catch (err) {
      console.error('[gift-popup] add error:', err);
      showError(modal, err && err.message ? err.message : 'Could not add to cart');
    } finally {
      modal._busy = false;
      if (cta) {
        cta.disabled = false;
        cta.setAttribute('aria-busy', 'false');
      }
    }
  }

  async function postItems(items) {
    const res = await fetch(ADD_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    });
    if (!res.ok) {
      const errPayload = await res.json().catch(function () { return {}; });
      throw new Error(errPayload.description || errPayload.message || 'Add to cart failed');
    }
    return res.json().catch(function () { return {}; });
  }

  function notifyCartUpdated(payload) {
    // Same event fan-out as deal-selector.js: `apps:product-added-to-cart` is
    // what refreshes the drawer markup + cart count in this theme.
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: payload } }));
    document.dispatchEvent(new CustomEvent('apps:product-added-to-cart', { detail: {} }));
  }

  function openQuickCart() {
    const cartIcon = document.querySelector('[data-js-cart-icon]');
    if (cartIcon) {
      setTimeout(function () {
        cartIcon.click();
      }, 300);
    }
  }

  function showError(modal, msg) {
    const el = modal.querySelector('[data-gift-popup-error]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = !msg;
  }
})();
